import { CASE_STATUS_OPTIONS, statusLabel as caseStatusLabel } from "./caseStatus";

// Shared metric-card engine.
//
// One place defines: the fields a metric card can filter on, how a filter is
// evaluated against a case document, how a card is counted, and how a
// "multilevel" card is grouped into subcards. Dashboard.jsx, MetricCases.jsx
// and MetricCardForm.jsx all import from here so the number shown on a card and
// the cases shown when you open it can never drift apart.
//
// Backward compatibility: older metricCard documents stored a single
// `field` (+ caseStatus / date / categoryId). getFilters() transparently
// converts those into the new `filters` array shape, so existing cards keep
// working without a data migration.

// ---- Option lists (single source of truth in utils/caseStatus.js) ----------

export const STATUS_OPTIONS = CASE_STATUS_OPTIONS;

export const APPEARING_FOR_OPTIONS = [
  { value: "petitioner", label: "Petitioner" },
  { value: "respondent", label: "Respondent" },
];

export const YES_NO_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

export const COUNTER_AFFIDAVIT_OPTIONS = [
  "Not Initiated",
  "Notice Received - Awaiting Instructions",
  "Para-wise Comments (PWC) Requested",
  "PWC Received from Department",
  "Drafting Under Preparation",
  "Draft Sent for Department Approval",
  "Vetted & Under Execution",
  "Filed (Physical/e-Filing)",
  "Served on Applicant/Parties",
  "Rejoinder Received",
].map((v) => ({ value: v, label: v }));

export const AUTHORITY_DEPARTMENT_OPTIONS = [
  "Residential", "Institutional", "IT", "HRD", "Water", "Urban Services",
  "Land", "Builders", "Group Housing", "Sewar", "Health", "University",
  "Finance", "Commercial", "6% Abadi Plot", "Others",
].map((v) => ({ value: v, label: v }));

export const TIME_UNITS = ["Day", "Week", "Month", "Quarter", "Year"];

// ---- Field registry --------------------------------------------------------
// kind drives which controls the filter row renders and how it is evaluated.
//   select   -> "is" / "is not" against a fixed option list
//   text     -> "contains" / "is" free-text match (case-insensitive)
//   date     -> time window / specific date / has value / no value
//   category -> matches a user label id (labels + labelsByUser)

export const METRIC_FIELDS = [
  { key: "status", label: "Case Status", kind: "select", options: STATUS_OPTIONS },
  { key: "caseType", label: "Case Type", kind: "text" },
  { key: "courtName", label: "Court", kind: "text" },
  { key: "caseYear", label: "Case Year", kind: "text" },
  { key: "appearingFor", label: "Appearing For", kind: "select", options: APPEARING_FOR_OPTIONS },
  { key: "counterAppealFiled", label: "Counter Appeal Filed", kind: "select", options: YES_NO_OPTIONS },
  { key: "counterAffidavitStatus", label: "Counter Affidavit Status", kind: "select", options: COUNTER_AFFIDAVIT_OPTIONS },
  { key: "authorityDepartment", label: "Authority Department", kind: "select", options: AUTHORITY_DEPARTMENT_OPTIONS },
  { key: "label", label: "Label / Category", kind: "category" },
  { key: "caseFiledDate", label: "Date of Filing", kind: "date" },
  { key: "nextHearingDate", label: "Next Hearing Date", kind: "date" },
  { key: "previousHearingDate", label: "Previous Hearing Date", kind: "date" },
  { key: "stayOrderDate", label: "Stay Order Date", kind: "date" },
  { key: "timestamp", label: "Created On", kind: "date" },
];

// Fields offered as the grouping dimension of a multilevel card.
export const SUBCARD_FIELDS = [
  { key: "status", label: "Case Status" },
  { key: "courtName", label: "Court" },
  { key: "caseType", label: "Case Type" },
  { key: "appearingFor", label: "Appearing For" },
  { key: "authorityDepartment", label: "Authority Department" },
  { key: "counterAffidavitStatus", label: "Counter Affidavit Status" },
  { key: "label", label: "Label / Category" },
];

export const getFieldDef = (key) => METRIC_FIELDS.find((f) => f.key === key);

export const DEFAULT_ACCENT = "#C99F4A";
export const DEFAULT_BG = "#FFFFFF";

// ---- Date helpers ----------------------------------------------------------

// Parse a "DD/MM/YYYY" string into a local-midnight Date, or null.
export function parseDMY(str) {
  if (!str || typeof str !== "string") return null;
  const parts = str.split("/");
  if (parts.length !== 3) return null;
  const [dd, mm, yyyy] = parts.map((p) => parseInt(p, 10));
  if (!dd || !mm || !yyyy) return null;
  return new Date(yyyy, mm - 1, dd);
}

// Resolve a case field to a date-only (midnight) Date, or null.
// `timestamp` is a Firestore Timestamp; the rest are DD/MM/YYYY strings.
function getDateValue(caseItem, key) {
  if (key === "timestamp") {
    const t = caseItem.timestamp;
    let d = null;
    if (t?.toDate) d = t.toDate();
    else if (t?.seconds != null) d = new Date(t.seconds * 1000);
    else if (t instanceof Date) d = t;
    return d ? new Date(d.getFullYear(), d.getMonth(), d.getDate()) : null;
  }
  return parseDMY(caseItem[key]);
}

function startOfToday() {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}

// Shift a date by n units (n can be negative).
function shiftDate(date, n, unit) {
  const d = new Date(date);
  switch (unit) {
    case "Day": d.setDate(d.getDate() + n); break;
    case "Week": d.setDate(d.getDate() + n * 7); break;
    case "Month": d.setMonth(d.getMonth() + n); break;
    case "Quarter": d.setMonth(d.getMonth() + n * 3); break;
    case "Year": d.setFullYear(d.getFullYear() + n); break;
    default: break;
  }
  return d;
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

// ---- Filter conversion (legacy -> new) -------------------------------------

// Return the card's filter array, converting an old single-field card if needed.
export function getFilters(card) {
  if (Array.isArray(card?.filters)) return card.filters;

  switch (card?.field) {
    case "caseStatus":
      return [{ field: "status", op: "is", value: card.caseStatus }];
    case "hearingInSevenDays":
      return [{ field: "nextHearingDate", mode: "window", direction: "next", n: 7, unit: "Day" }];
    case "created on":
      return [{ field: "caseFiledDate", mode: "on", value: card.date }];
    case "hasNextDate":
      return [{ field: "nextHearingDate", mode: "exists" }];
    case "byCategory":
      return [{ field: "label", op: "has", value: card.categoryId }];
    default:
      return [];
  }
}

// ---- Evaluation ------------------------------------------------------------

function evaluateFilter(caseItem, filter, ctx) {
  const def = getFieldDef(filter.field);
  if (!def) return true;

  if (def.kind === "date") {
    const dv = getDateValue(caseItem, filter.field);
    switch (filter.mode) {
      case "exists": return dv != null;
      case "missing": return dv == null;
      case "on": {
        const target = parseISO(filter.value);
        return dv != null && target != null && sameDay(dv, target);
      }
      case "window": {
        if (dv == null) return false;
        const base = startOfToday();
        const n = Number(filter.n) || 0;
        if (filter.direction === "last") {
          const lower = shiftDate(base, -n, filter.unit);
          const upper = shiftDate(base, 1, "Day"); // include all of today
          return dv >= lower && dv < upper;
        }
        // next (default)
        const upper = shiftDate(base, n, filter.unit);
        return dv >= base && dv <= upper;
      }
      default: return true;
    }
  }

  if (def.kind === "category") {
    const id = filter.value;
    if (!id) return true;
    const caseLabels = caseItem.labels || [];
    if (caseLabels.includes(id)) return true;
    const byUser = (caseItem.labelsByUser || {})[ctx?.uid] || [];
    return byUser.includes(id);
  }

  // select / text
  const raw = caseItem[filter.field];
  const cv = raw == null ? "" : String(raw);
  const target = filter.value == null ? "" : String(filter.value);
  if (target === "") return true;

  if (def.kind === "select") {
    return filter.op === "isNot" ? cv !== target : cv === target;
  }
  // text
  if (filter.op === "is") return cv.toLowerCase() === target.toLowerCase();
  return cv.toLowerCase().includes(target.toLowerCase());
}

function parseISO(value) {
  if (!value || typeof value !== "string") return null;
  const [y, m, d] = value.split("-").map((p) => parseInt(p, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

// A case matches a card when it passes every filter (AND). No filters => match all.
export function caseMatchesCard(caseItem, card, ctx) {
  const filters = getFilters(card);
  if (filters.length === 0) return true;
  return filters.every((f) => evaluateFilter(caseItem, f, ctx));
}

export function countForCard(cases, card, ctx) {
  if (!Array.isArray(cases)) return 0;
  return cases.reduce((n, c) => (caseMatchesCard(c, card, ctx) ? n + 1 : n), 0);
}

export function filterCases(cases, card, ctx) {
  if (!Array.isArray(cases)) return [];
  return cases.filter((c) => caseMatchesCard(c, card, ctx));
}

// ---- Multilevel grouping ---------------------------------------------------

// How many subcard tiles a multilevel card shows before collapsing the rest
// into a single "+N more" tile.
export const SUBCARD_LIMIT = 8;

// Group the cases that match a card by the card's subcardField.
// labelsMap: { [labelId]: name } – used to label "Label / Category" subcards.
// Returns { groups: [{ key, label, value }] (top SUBCARD_LIMIT, count desc),
//           more: number of groups beyond the limit }.
export function groupForSubcard(cases, card, ctx, labelsMap = {}) {
  const field = card?.subcardField;
  if (!field) return { groups: [], more: 0 };
  const matched = filterCases(cases, card, ctx);
  const counts = new Map();

  const bump = (key, label) => {
    const existing = counts.get(key);
    if (existing) existing.value += 1;
    else counts.set(key, { key, label, value: 1 });
  };

  matched.forEach((c) => {
    if (field === "label") {
      const ids = new Set([
        ...(c.labels || []),
        ...(((c.labelsByUser || {})[ctx?.uid]) || []),
      ]);
      if (ids.size === 0) return;
      ids.forEach((id) => bump(id, labelsMap[id] || "Label"));
    } else {
      const raw = c[field];
      const val = raw == null || raw === "" ? "—" : String(raw);
      bump(val, statusLabel(field, val));
    }
  });

  const sorted = Array.from(counts.values()).sort((a, b) => b.value - a.value);
  return {
    groups: sorted.slice(0, SUBCARD_LIMIT),
    more: Math.max(0, sorted.length - SUBCARD_LIMIT),
  };
}

function statusLabel(field, value) {
  if (field === "status") {
    return caseStatusLabel(value);
  }
  return value;
}

// ---- Human-readable summary (for tooltips / titles) ------------------------

export function describeCard(card) {
  const filters = getFilters(card);
  if (filters.length === 0) return "All cases";
  return filters.map(describeFilter).join("  •  ");
}

function describeFilter(f) {
  const def = getFieldDef(f.field);
  const name = def?.label || f.field;
  if (def?.kind === "date") {
    switch (f.mode) {
      case "exists": return `${name}: has value`;
      case "missing": return `${name}: no value`;
      case "on": return `${name} on ${f.value || "?"}`;
      case "window": return `${name}: ${f.direction === "last" ? "last" : "next"} ${f.n} ${f.unit}`;
      default: return name;
    }
  }
  if (def?.kind === "category") return `${name} selected`;
  const op = f.op === "isNot" ? "is not" : f.op === "contains" ? "contains" : "is";
  return `${name} ${op} ${f.value}`;
}
